import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, EyeOffIcon } from 'lucide-react';
import { Density, Field } from '../types';
import { InlineField } from './InlineField';
import { resolveIcon } from '../utils/icons';
interface FormSectionProps {
  /** DOM id used as the scroll anchor from the completion rail */
  anchorId: string;
  title: string;
  icon: string;
  fields: Field[];
  columns: 1 | 2;
  density: Density;
  defaultCollapsed: boolean;
  /** Count of fields currently hidden by admin config or rules */
  hiddenCount: number;
  /** Field ids that count toward record completion */
  requiredIds: Set<string>;
  values: Record<string, string | boolean>;
  dirtyIds: Set<string>;
  onChange: (id: string, value: string | boolean) => void;
}
export function FormSection({
  anchorId,
  title,
  icon,
  fields,
  columns,
  density,
  defaultCollapsed,
  hiddenCount,
  requiredIds,
  values,
  dirtyIds,
  onChange
}: FormSectionProps) {
  const [open, setOpen] = useState(!defaultCollapsed);
  const Icon = resolveIcon(icon);
  const compact = density === 'compact';
  useEffect(() => {
    setOpen(!defaultCollapsed);
  }, [defaultCollapsed]);
  const editedCount = fields.filter(f => dirtyIds.has(f.id)).length;
  return <section id={anchorId} className="scroll-mt-44 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-id="element-198" data-mp="22zu4rxgru5bc">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className={`flex w-full items-center gap-3 text-left transition-colors hover:bg-slate-50 ${compact ? 'px-4 py-3' : 'px-5 py-4'}`} data-id="element-199" data-mp="2zn7ecgo3jwnb">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600" data-id="element-200" data-mp="2lor084nwn3v5">
          <Icon className="h-[18px] w-[18px]" data-id="element-201" data-mp="1farmqoi86agb" />
        </span>
        <div className="min-w-0 flex-1" data-id="element-202" data-mp="1aa3vq2tzxtm3">
          <h2 className="text-[15px] font-semibold text-slate-900" data-id="element-203" data-mp="1a92chb2tu0ta">{title}</h2>
          <p className="flex items-center gap-1.5 text-xs text-slate-400" data-id="element-204" data-mp="1bsu2z0slewzg">
            {fields.length} fields
            {hiddenCount > 0 && <span className="inline-flex items-center gap-1 text-slate-400" data-id="element-205" data-mp="12qunwe1522z5">
                <EyeOffIcon className="h-3 w-3" data-id="element-206" data-mp="12sxf31e1225g" />
                {hiddenCount} hidden
              </span>}
          </p>
        </div>
        {editedCount > 0 && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700" data-id="element-207" data-mp="szgkvw9zal19">
            {editedCount} edited
          </span>}
        <ChevronDownIcon className={`h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-208" data-mp="j3mx1gqt7546" />
      </button>

      <AnimatePresence initial={false} data-id="element-209" data-mp="a1ngsqnmc52a">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-210" data-mp="a49uz950lith">
            <div className={`border-t border-slate-100 ${compact ? 'p-1.5' : 'p-2 sm:p-3'}`} data-id="element-211" data-mp="3mnm1h1fvf91v">
              {fields.length === 0 ? <p className="px-3 py-8 text-center text-sm text-slate-400" data-id="element-212" data-mp="et7eq5bidfw">
                  No visible fields in this section.
                </p> : <div className={`grid grid-cols-1 gap-0.5 ${columns === 2 ? 'md:grid-cols-2' : ''}`} data-id="element-213" data-mp="ec9g68b36jat">
                  {fields.map(field => <InlineField key={field.id} field={field} value={values[field.id]} dirty={dirtyIds.has(field.id)} compact={compact} required={requiredIds.has(field.id)} onChange={onChange} data-id="element-214" data-mp="j52hsnb9dbio" />)}
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}